import { useState } from "react";
import { RiStarFill } from "react-icons/ri";
import { useTranslation } from "react-i18next";
import { useApi, useErrorDialog } from "../../../../hooks";
import { IFavoriteType } from "../types/howtofix_types";
import {
  deleteFavoriteInfo,
  registerFavoriteInfo,
} from "../../../api/dts_api_wrapper";
import { FaBookmark, FaRegBookmark } from "react-icons/fa6";

interface FavoriteManualProps {
  userId: string;
  reference: string;
  favoriteInfo: IFavoriteType;
  isFavorite: boolean;
  setIsFavorite: React.Dispatch<React.SetStateAction<boolean>>;
  setFavoriteInfo: React.Dispatch<React.SetStateAction<IFavoriteType>>;
}

// マニュアルのお気に入り登録・削除を制御する
export const FavoriteManual: React.FC<FavoriteManualProps> = ({
  userId,
  reference,
  favoriteInfo,
  isFavorite,
  setIsFavorite,
  setFavoriteInfo,
}) => {
  //言語切替用
  const { t } = useTranslation();
  //API用
  const { handleApi } = useApi();

  //localStorageから型式, マニュアルタイトルを取得(localStorageからlsを接頭辞とする)
  const lsModel = localStorage.getItem("model") ?? "";
  const lsTitle = localStorage.getItem("title") ?? "";

  // お気に入り登録・削除処理中かどうか
  const [isRegistering, setIsRegistering] = useState<boolean>(false);

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  //お気に入りボタン押下時の処理
  const handleOnFavoriteButton = async () => {
    // お気に入り処理中の場合は何もしない
    if (isRegistering) return;

    // 必要なパラメータが不足している場合、エラーダイアログを表示してタブを閉じる
    try {
      if (isFavorite) {
        //お気に入りに登録されている場合, お気に入り情報削除のAPIのパラメータをチェック
        if (!userId || !favoriteInfo.id) {
          throw new Error("failed to delete favorite info");
        }
      } else {
        //お気に入りに登録されていない場合, お気に入り情報登録のAPIのパラメータをチェック
        if (!userId || !reference || !lsModel || !lsTitle) {
          throw new Error("failed to regist favorite info");
        }
      }
    } catch (err) {
      //必要なパラメータが取得できなかったら, タブを閉じる
      console.log(`[How To Fix] handleOnFavoriteButton error:`, err);
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "closeTab"
      ); //エラー", "通信エラーが発生しました。(タブを閉じる)
      return; // パラメータ不足の場合は処理を終了
    }

    // お気に入り処理開始
    setIsRegistering(true);
    try {
      if (isFavorite) {
        //お気に入りに登録されている場合, 削除
        const isDelete = await handleDeleteFavoriteInfo();
        if (isDelete) {
          //お気に入りボタンの表示を非登録状態にする
          setIsFavorite(false);
        } else {
          throw new Error("failed to delete favorite");
        }
      } else {
        //お気に入りに登録されていない場合, 登録
        const isRegister = await handleRegisterFavoriteInfo();
        if (isRegister) {
          //お気に入りボタンの表示を登録状態にする
          setIsFavorite(true);
        } else {
          throw new Error("failed to register favorite");
        }
      }
    } catch (err) {
      console.log(`[How To Fix] handleOnFavoriteButton error:`, err);
      if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "closeTab"
        ); //エラー", "セッションがタイムアウトしました。(タブを閉じる)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。")
        ); //エラー", "通信エラーが発生しました。
      }
    } finally {
      //お気に入り処理終了
      setIsRegistering(false);
    }
  };

  //お気に入り情報の登録処理
  const handleRegisterFavoriteInfo = async () => {
    //お気に入り情報登録
    const registerFavoriteInfoResult = await handleApi(
      registerFavoriteInfo,
      userId,
      reference,
      lsModel,
      lsTitle
    );
    //お気に入り情報登録でエラーの場合
    if ("errorMessage" in registerFavoriteInfoResult) {
      console.log("errorMessage: ", registerFavoriteInfoResult.errorMessage);
      throw new Error("failed to register favorite");
    }

    const registerFavoriteInfoStr = JSON.stringify(registerFavoriteInfoResult);
    const registerFavoriteInfoJson: { id: number } = JSON.parse(
      registerFavoriteInfoStr
    );
    // 登録結果(id)と登録時のパラメータをstateに格納
    setFavoriteInfo((prevState) => ({
      ...prevState,
      id: registerFavoriteInfoJson.id,
      model: lsModel,
      title: lsTitle,
    }));
    //処理が正常終了した場合, trueを返す
    return true;
  };

  //お気に入り情報の削除処理
  const handleDeleteFavoriteInfo = async () => {
    //お気に入り情報削除
    const deleteFavoriteInfoResult = await handleApi(
      deleteFavoriteInfo,
      userId,
      favoriteInfo.id
    );
    //お気に入り情報登録でエラーの場合
    if ("errorMessage" in deleteFavoriteInfoResult) {
      console.log("errorMessage: ", deleteFavoriteInfoResult.errorMessage);
      throw new Error("failed to delete favorite");
    }
    //お気に入り情報のstate値を初期化
    setFavoriteInfo({
      id: 0,
      model: "",
      title: "",
      displayDateTime: "",
    });
    //処理が正常終了した場合, trueを返す
    return true;
  };

  return (
    <button
      onClick={handleOnFavoriteButton}
      className="p-2"
      disabled={isRegistering}
    >
      {/* Favoriteボタン */}
      {isFavorite ? (
        <FaBookmark className="fill-red-500 stroke-sub-red size-[30px]" />
      ) : (
        <FaRegBookmark className="fill-red-500 size-[30px]" />
      )}
    </button>
  );
};
